<template>
  <div>
    <div id="management-portal" v-if="this.$keycloak.authenticated && isUserDataLoaded && !userDataLoadError">
      <div v-if="$route.meta.layout === 'blank'">
        <v-app>
          <router-view :key="$route.path"></router-view>
        </v-app>
      </div>
      <div v-else>
        <v-app>
          <v-app-bar
            color="primary"
            app>

            <v-app-bar-nav-icon 
              aria-label="Sidebar Menu Button" 
              @click.stop="onBurgerBtnClicked" />
            <v-toolbar-title>{{ toolbarTitle }}</v-toolbar-title>
          </v-app-bar>

          <v-navigation-drawer 
            v-model="drawer"
            :permanent="permanentDrawer"
            :mini-variant.sync="miniDrawer"
            :temporary="temporaryDrawer"
            color="primary"
            app
          >
            
            <v-list-item>
              <v-list-item-content>
                <v-img id="logo" contain :src="activeLogo"></v-img>
              </v-list-item-content>
            </v-list-item>

            <v-divider></v-divider>

            <v-list
              dense
              nav>
              <v-list-item
                v-for="item in menuList"
                :key="item.slug"
                link
                :to="'/' + item.slug"
                color="accent"
              >
                <v-list-item-icon>
                  <v-icon>{{ icons[item.icon] }}</v-icon>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <template v-slot:append>
              <v-list
                dense
                nav>
                <v-list-item @click="onClickLogout">
                  <v-list-item-icon>
                    <v-icon>{{ icons.mdiLogout }}</v-icon>
                  </v-list-item-icon>

                  <v-list-item-content>
                    <v-list-item-title>Logout</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>
          </v-navigation-drawer>
          
          <v-main>
            <v-container fluid class="ma-0">
              
              <v-row>
                <v-col class="d-flex justify-center" 
                  cols="12" sm="12" md="12" lg="12" xl="12">
                  <v-sheet
                    class="pa-8 ma-3"
                    min-height="200"
                    width="100%"
                    elevation="8"
                    rounded="lg"
                  >
                    <v-slide-x-transition mode="out-in">
                      <router-view :key="$route.path"></router-view>
                    </v-slide-x-transition>
                  </v-sheet>
                </v-col>
              </v-row>
              
            </v-container>
          </v-main>
          <portal-footer id="management-portal-footer" ></portal-footer>
          <global-snackbar></global-snackbar>
        </v-app>
      </div>
    </div>
    <div
      v-else-if="this.$keycloak.authenticated && isUserDataLoaded && userDataLoadError"
      class="user-data-error-screen"
    >
      <v-app>
        <v-main class="user-data-error-screen__main d-flex align-center justify-center pa-8">
          <div class="user-data-error-screen__inner text-center">
            <div class="text-h4 text-md-h3 mb-6">Something went wrong</div>
            <p class="text-h6 font-weight-regular mb-10">{{ userDataLoadError }}</p>
            <div class="d-flex flex-wrap justify-center align-center" style="gap: 12px;">
              <v-btn color="primary" x-large depressed @click="retryLoadAccount">
                Try again
              </v-btn>
              <v-btn x-large outlined @click="onClickLogout">
                <v-icon left>{{ icons.mdiLogout }}</v-icon>
                Logout
              </v-btn>
            </div>
          </div>
        </v-main>
      </v-app>
    </div>
    <v-progress-linear
      v-else
      buffer-value="0"
      stream
      reverse
      color="primary"
    ></v-progress-linear>

    <popup-message></popup-message>
  </div>
</template>

<script>
import realsamLogo from './assets/images/realsam-logo-negative.png'
import footer from './views/snippets/FooterSection.vue'
import PopupMessage from './components/PopupMessage.vue';
import GlobalSnackbar from './components/GlobalSnackbar.vue';
import { mapState } from 'vuex'
import { mdiViewDashboard, mdiCurrencyUsd, mdiBookMultiple, mdiContacts, 
  mdiAccountCog, mdiAccountGroup, mdiMessage, mdiGoogleHome, mdiCogs, 
  mdiCellphone, mdiCellphoneWireless, mdiOfficeBuilding, mdiLogout,
  mdiBallot, mdiRocketLaunch, mdiCart, mdiMonitorMultiple, mdiApplicationBrackets} from '@mdi/js'

export default {
  name: 'App',
  components: {
    portalFooter: footer,
    popupMessage: PopupMessage,
    globalSnackbar: GlobalSnackbar,
  },
  data: () => ({
    imgs: {
      realsamLogo: realsamLogo
    },
    icons: {
      mdiViewDashboard: mdiViewDashboard,
      mdiCurrencyUsd: mdiCurrencyUsd,
      mdiBookMultiple: mdiBookMultiple,
      mdiContacts: mdiContacts,
      mdiAccountCog: mdiAccountCog,
      mdiAccountGroup: mdiAccountGroup,
      mdiMessage: mdiMessage,
      mdiGoogleHome: mdiGoogleHome,
      mdiCogs: mdiCogs,
      mdiCellphone: mdiCellphone,
      mdiCellphoneWireless: mdiCellphoneWireless,
      mdiOfficeBuilding: mdiOfficeBuilding,
      mdiLogout: mdiLogout,
      mdiBallot: mdiBallot,
      mdiRocketLaunch: mdiRocketLaunch,
      mdiCart: mdiCart,
      mdiMonitorMultiple: mdiMonitorMultiple,
      mdiApplicationBrackets: mdiApplicationBrackets
    },
    drawer: true,
    permanentDrawer: true,
    miniDrawer: false,
    temporaryDrawer: false,
    menuList: [],
    isUserDataLoaded: false,
    toolbarTitle: "Management Portal",
    logo: realsamLogo,
    logoDark: null,
  }),
  created() {
    this.$root.buildUserMenu()
  },
  mounted() {
    if (this.$vuetify.breakpoint.sm || this.$vuetify.breakpoint.xs) {
      this.permanentDrawer = false
      this.temporaryDrawer = true
      this.miniDrawer = false
      this.drawer = false
    }
    this.$vuetify.theme.dark = this.$root.getLocalStorage("isDarkTheme") === "true"
  },
  methods: {
    onBurgerBtnClicked() {
      if (this.$vuetify.breakpoint.sm || this.$vuetify.breakpoint.xs) {
        this.permanentDrawer = false
        this.temporaryDrawer = true
        this.miniDrawer = false
        this.drawer = !this.drawer
      } else {
        this.permanentDrawer = true
        this.temporaryDrawer = false
        this.miniDrawer = !this.miniDrawer
        this.drawer = true
      }
    },
    onClickLogout() {
      localStorage.removeItem("menu")
      this.$keycloak.logout();
    },
    retryLoadAccount() {
      this.isUserDataLoaded = false
      this.$store.commit("setUserDataLoadError", null)
      this.$root.buildUserMenu()
    },
  },
  computed: {
    ...mapState({
      userDataLoadError: (state) => state.userDataLoadError
    }),
    activeLogo() {
      if (this.$vuetify.theme.dark && this.logoDark) return this.logoDark
      return this.logo
    },
    isDrawerPermanent() {
      return this.drawer && !this.$vuetify.breakpoint.sm
    },
    isDrawerMini() {
      return this.miniDrawer || this.$vuetify.breakpoint.sm
    },
    isDrawerTemporary() {
      return this.temporaryDrawer && this.$vuetify.breakpoint.sm
    }
  },
  watch: {
    '$store.state.userMenu' (newMenu) {
      this.menuList = newMenu.filter(e => {
        return e.isHidden !== true
      })
    },
    '$store.state.userData' (newUserData) {
      if (newUserData) this.isUserDataLoaded = true
    },
    '$store.state.userDataLoadError' (msg) {
      console.log("test", msg)
      if (msg) this.isUserDataLoaded = true
    },
    '$store.state.org.portalName' (newValue) {
      if (newValue) this.toolbarTitle = newValue
    },
    '$store.state.org.logoUrl' (newValue) {
      if (newValue) this.logo = newValue
    },
    '$store.state.org.logoUrlDark' (newValue) {
      this.logoDark = newValue || null
    }
  }
};
</script>
<style lang="scss" scoped>
.user-data-error-screen {
  min-height: 100vh;
  width: 100%;
}
.user-data-error-screen__main {
  min-height: inherit;
}
.user-data-error-screen__inner {
  max-width: 52rem;
  margin-left: auto;
  margin-right: auto;
}
</style>
